import React, { useState, useEffect, useCallback, useMemo } from 'react';
import acessoService from '../../services/acesso.service';
import colaboradorService from '../../services/colaborador.service';
import clienteService from '../../services/cliente.service';
import BtnIcone from '../../components/BtnIcone';
import SePode from '../../components/SePode';
import BrowseHeader from '../../components/BrowseHeader';                       // r105
import { iconeDaRotina } from '../../constants/iconesRotinas';                  // r105
import { usePermissoes } from '../../context/PermissoesContext';   // r100
import { useAuth } from '../../context/AuthContext';                    // r-now
import AcessoModal from './AcessoModal';
import {
  TIPOS_ACESSO, LABELS_ATRIBUTO, ehSenha, iconeDoTipo,
  VISIBILIDADES, VISIBILIDADE_PADRAO,
} from '../../constants/tiposAcesso';

// ════════════════════════════════════════════════════════════════════════════
// AcessosPage.jsx — r93
// ────────────────────────────────────────────────────────────────────────────
// Layout do PDF: 2 colunas
//   Grid 01 (esquerda):  agrupado por usuário + cliente (1 linha por par)
//                        ao clicar numa linha, mostra acessos na Grid 02
//   Grid 02 (direita):   lista de acessos do par selecionado, expansível
//                        cada acesso mostra atributos quando expandido
//
// Filtros no topo: multi-seleção de usuário e cliente.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    bg:      '#f0f2f7',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
};

const inputStyle = {
  // r95: alinhado ao padrão do cadastro de Clientes — sem fontFamily forçado
  //      (herda 'Manrope' do container pai) e com boxSizing pra evitar overflow.
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};

// r95: selectStyle igual ao do Clientes — herda inputStyle + cursor pointer.
//      Sem appearance:none, o select usa a setinha padrão do browser
//      (consistente com o resto do app).
const selectStyle = { ...inputStyle, cursor: 'pointer' };

function AcessosPage() {
  // r100: lê filtro próprio da rotina pra esconder MultiSelect 'Usuário' quando ativo
  const { filtroDaRotina, isAdmin } = usePermissoes();
  const { usuario } = useAuth();
  const escondeFiltroColaborador = !isAdmin && filtroDaRotina('acessos') === 'meu_colaborador';

  // r-now: quem PODE ver acessos de outros colaboradores entra na tela já filtrado
  //        no próprio colaborador (é o caso de uso mais comum), podendo desligar
  //        o "Somente meus" pra enxergar os demais. Quem já tem o filtro próprio
  //        'meu_colaborador' não vê o toggle — o backend restringe de qualquer jeito.
  const [meuColaboradorId, setMeuColaboradorId] = useState(null);
  const [soMeus, setSoMeus] = useState(false);
  // Só carrega a lista depois de resolver o "meu colaborador", pra não fazer uma
  // primeira busca sem o filtro padrão (e piscar acessos de todo mundo na tela).
  const [filtroInicialPronto, setFiltroInicialPronto] = useState(false);

  const [acessos, setAcessos] = useState([]);
  // r-now: começa 'carregando' porque a 1ª busca só dispara depois de resolver
  //         o filtro padrão — evita piscar 'Nenhum acesso cadastrado.'
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Filtros multi-seleção
  const [colaboradoresLista, setColaboradoresLista] = useState([]);
  const [clientesLista, setClientesLista] = useState([]);
  const [filtroColaboradorIds, setFiltroColaboradorIds] = useState([]);
  const [filtroClienteIds, setFiltroClienteIds] = useState([]);

  // Seleção do par colaborador+cliente (grid esquerda)
  const [parSelecionado, setParSelecionado] = useState(null);   // { colaborador_id, cliente_id }

  // Acessos expandidos na grid direita
  const [expandidos, setExpandidos] = useState(new Set());

  // Modal
  const [modalAberto, setModalAberto] = useState(false);
  const [modoModal, setModoModal] = useState('novo');
  const [acessoEdit, setAcessoEdit] = useState(null);

  // ── Carrega listas de filtros (1x) ──────────────────────────────────────
  // r154: usa Promise.allSettled pra não quebrar a tela se o usuário não tiver
  //       permissão de visualizar colaboradores ou clientes. Cada lista é
  //       opcional pros filtros (são só dropdowns), então degradar é melhor que
  //       falhar a tela toda.
  useEffect(() => {
    (async () => {
      const [resColab, resCli] = await Promise.allSettled([
        colaboradorService.listar(),
        clienteService.listar(),
      ]);
      if (resColab.status === 'fulfilled') {
        setColaboradoresLista(resColab.value || []);
      } else {
        console.warn('Acessos: sem permissão pra listar colaboradores — filtro fica vazio.', resColab.reason?.message);
      }
      if (resCli.status === 'fulfilled') {
        setClientesLista(resCli.value || []);
      } else {
        console.warn('Acessos: sem permissão pra listar clientes — filtro fica vazio.', resCli.reason?.message);
      }
    })();
  }, []);

  // ── Resolve o colaborador do usuário logado (filtro padrão "Somente meus") ──
  // r-now: usa /colaboradores/referencia (ROTA_AUTENTICADA, sem 403 e sem o
  //        filtro "só os meus") — o endpoint devolve usuario_id justamente pra
  //        casar o colaborador com o usuário logado.
  useEffect(() => {
    if (escondeFiltroColaborador) { setFiltroInicialPronto(true); return; }
    let vivo = true;
    (async () => {
      try {
        const cols = await colaboradorService.listarReferencia();
        const meu = (cols || []).find(c => c.usuario_id && c.usuario_id === usuario?.id);
        if (!vivo) return;
        if (meu) {
          setMeuColaboradorId(meu.id);
          setSoMeus(true);                    // entra na tela já filtrado no próprio
        }
      } catch (err) {
        console.warn('Acessos: não foi possível resolver o colaborador do usuário logado.', err.message);
      } finally {
        if (vivo) setFiltroInicialPronto(true);
      }
    })();
    return () => { vivo = false; };
  }, [usuario?.id, escondeFiltroColaborador]);

  // ── Carrega acessos ─────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    if (!filtroInicialPronto) return;
    setErro('');
    try {
      setCarregando(true);
      const data = await acessoService.listar({
        // r-now: com "Somente meus" ligado, o filtro de colaborador é o próprio
        //        (o MultiSelect fica escondido enquanto isso).
        colaborador_ids: soMeus && meuColaboradorId ? [meuColaboradorId] : filtroColaboradorIds,
        cliente_ids: filtroClienteIds,
      });
      setAcessos(data || []);
    } catch (err) {
      setErro('Erro ao carregar: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setCarregando(false);
    }
  }, [filtroColaboradorIds, filtroClienteIds, soMeus, meuColaboradorId, filtroInicialPronto]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Agrupa por (usuário, cliente) pra grid 01 ───────────────────────────
  const pares = useMemo(() => {
    const map = new Map();
    for (const a of acessos) {
      const chave = `${a.colaborador_id}__${a.cliente_id}`;
      if (!map.has(chave)) {
        map.set(chave, {
          chave,
          colaborador_id:   a.colaborador_id,
          cliente_id:       a.cliente_id,
          colaborador_nome: a.colaborador_nome,
          cliente_nome: a.cliente_nome,
          cliente_tag:  a.cliente_tag,
          cliente_cor_tag: a.cliente_cor_tag,
          quantidade:   0,
        });
      }
      map.get(chave).quantidade++;
    }
    return Array.from(map.values());
  }, [acessos]);

  // ── Acessos do par selecionado ──────────────────────────────────────────
  const acessosDoPar = useMemo(() => {
    if (!parSelecionado) return [];
    return acessos.filter(a =>
      a.colaborador_id === parSelecionado.colaborador_id &&
      a.cliente_id === parSelecionado.cliente_id
    );
  }, [acessos, parSelecionado]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const abrirNovo = () => {
    setModoModal('novo');
    setAcessoEdit(null);
    setModalAberto(true);
  };

  const abrirEditar = (a) => {
    setModoModal('editar');
    setAcessoEdit(a);
    setModalAberto(true);
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir o acesso "${a.descricao}" (${a.seq})?`)) return;
    try {
      await acessoService.excluir(a.id);
      carregar();
    } catch (err) {
      setErro('Erro ao excluir: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  const toggleExpandir = (id) => {
    setExpandidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // r-now: liga/desliga o filtro padrão no próprio colaborador
  const togglarSoMeus = () => {
    setSoMeus(v => {
      if (!v) setFiltroColaboradorIds([]);   // ao ligar, zera a multi-seleção manual
      return !v;
    });
    setParSelecionado(null);                 // a grid 2 aponta pro par antigo
  };

  const togglarFiltroMulti = (setter, lista, id) => {
    setter(lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id]);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{
      padding: '16px 24px', backgroundColor: COR.bg,
      minHeight: 'calc(100vh - 100px)', fontFamily: "'Manrope', sans-serif",
    }}>
      {/* r105: BrowseHeader padronizado — ícone SVG da rotina + título + descrição */}
      <BrowseHeader
        rotina="acessos"
        titulo="Acessos"
        descricao="Credenciais de acesso aos ambientes dos clientes por colaborador."
      />

      {/* Filtros + botão Novo alinhado ao limite direito da Grid 1 (1fr | 1.4fr) */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '14px',
        marginBottom: '16px', alignItems: 'flex-end',
      }}>
        {/* Coluna 1: filtros + botão Novo na ponta direita */}
        <div style={{
          display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap',
        }}>
          {/* r-now: filtro padrão "Somente meus" pra quem pode ver de outros */}
          {!escondeFiltroColaborador && meuColaboradorId && (
            <label style={{
              height: '36px', display: 'flex', alignItems: 'center', gap: '6px',
              padding: '0 10px', cursor: 'pointer',
              border: `1px solid ${soMeus ? COR.azul : COR.borda}`, borderRadius: '5px',
              backgroundColor: soMeus ? COR.azulBg : COR.branco,
              fontSize: '13px', fontWeight: '600',
              color: soMeus ? COR.azul : COR.texto, whiteSpace: 'nowrap',
            }} title="Mostrar apenas os acessos do meu colaborador">
              <input type="checkbox" checked={soMeus} onChange={togglarSoMeus} />
              <span>Somente meus</span>
            </label>
          )}
          {/* r100: esconde MultiSelect 'Usuário' quando o filtro próprio "meus_acessos" está ativo */}
          {/* r-now: e também enquanto "Somente meus" estiver ligado (o filtro é o próprio) */}
          {!escondeFiltroColaborador && !soMeus && (
            <MultiSelect
              label="Colaborador"
              opcoes={colaboradoresLista.filter(c => c.status === 'A').map(c => ({
                id: c.id, label: c.nome_amigavel || c.nome,
              }))}
              selecionados={filtroColaboradorIds}
              onToggle={id => togglarFiltroMulti(setFiltroColaboradorIds, filtroColaboradorIds, id)}
            />
          )}
          <MultiSelect
            label="Cliente"
            opcoes={clientesLista.filter(c => c.status === 'A').map(c => ({
              id: c.id, label: c.nome_fantasia || c.razao_social || c.nome,
            }))}
            selecionados={filtroClienteIds}
            onToggle={id => togglarFiltroMulti(setFiltroClienteIds, filtroClienteIds, id)}
          />
          {/* Empurra o botão pro fim da coluna (limite direito da Grid 1) */}
          <div style={{ marginLeft: 'auto' }}>
            <SePode rotina="acessos" acao="incluir">
              <BtnIcone icone="incluir" onClick={abrirNovo} title="Novo acesso"
                        cor={COR.azul} solido />
            </SePode>
          </div>
        </div>
        {/* Coluna 2 vazia (a Grid 2 abaixo ocupa esse espaço) */}
        <div />
      </div>

      {/* Erro */}
      {erro && (
        <div style={{
          padding: '10px 14px', backgroundColor: COR.vermBg,
          color: COR.vermelho, fontSize: '13px', fontWeight: '600',
          border: `1px solid ${COR.vermelho}`, borderRadius: '5px',
          marginBottom: '12px',
        }}>{erro}</div>
      )}

      {/* 2 grids lado a lado */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '14px',
      }}>
        {/* Grid 01 — pares usuário/cliente */}
        <div style={{
          backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
          borderRadius: '6px', overflow: 'hidden',
        }}>
          <div style={{
            backgroundColor: COR.azul, color: COR.branco,
            padding: '10px 14px', fontSize: '12px', fontWeight: '700',
            // r-now: largura fixa pra última coluna + borda esquerda invisível
            //        pra alinhar com as linhas (que têm borderLeft 3px de seleção)
            display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: '10px',
            borderLeft: '3px solid transparent',
          }}>
            <div>Colaborador</div>
            <div>Cliente</div>
            <div style={{ textAlign: 'right' }}>Ações</div>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            {carregando ? (
              <div style={{ padding: '20px', textAlign: 'center', color: COR.muted }}>
                Carregando...
              </div>
            ) : pares.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: COR.muted }}>
                Nenhum acesso cadastrado.
              </div>
            ) : pares.map(p => {
              const ativo = parSelecionado?.colaborador_id === p.colaborador_id
                         && parSelecionado?.cliente_id === p.cliente_id;
              return (
                <div key={p.chave}
                  onClick={() => setParSelecionado({ colaborador_id: p.colaborador_id, cliente_id: p.cliente_id })}
                  style={{
                    padding: '10px 14px', fontSize: '13px',
                    borderBottom: `1px solid ${COR.borda}`,
                    backgroundColor: ativo ? COR.azulBg : COR.branco,
                    borderLeft: ativo ? `3px solid ${COR.azul}` : '3px solid transparent',
                    cursor: 'pointer',
                    // r-now: 1fr 1fr 90px alinhado com o header
                    display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: '10px',
                    alignItems: 'center',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* r105: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                    {iconeDaRotina('acessos', { width: 20, height: 20, color: COR.azul })}
                    <span style={{ color: COR.texto }}>{p.colaborador_nome}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {/* r94: só a tag do cliente, mesmo formato/cores do cadastro */}
                    <TagCliente tag={p.cliente_tag} cor={p.cliente_cor_tag} />
                  </div>
                  <div style={{ fontSize: '11px', color: COR.muted, textAlign: 'right' }}>
                    {p.quantidade} {p.quantidade === 1 ? 'acesso' : 'acessos'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grid 02 — acessos do par selecionado */}
        <div style={{
          backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
          borderRadius: '6px', overflow: 'hidden',
        }}>
          <div style={{
            backgroundColor: COR.azul, color: COR.branco,
            padding: '10px 14px', fontSize: '12px', fontWeight: '700',
            // r-now: última coluna fixa em 100px (≈ alterar+excluir+expandir+gap)
            // r245: + coluna de visibilidade (130px) antes das ações
            display: 'grid', gridTemplateColumns: '60px 1fr 2fr 130px 100px', gap: '10px',
          }}>
            <div>Seq</div>
            <div>Acesso</div>
            <div>Descrição</div>
            <div>Visibilidade</div>
            <div></div>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            {!parSelecionado ? (
              <div style={{ padding: '30px', textAlign: 'center', color: COR.muted, fontSize: '13px' }}>
                Selecione um usuário/cliente na lista ao lado pra ver os acessos.
              </div>
            ) : acessosDoPar.length === 0 ? (
              <div style={{ padding: '30px', textAlign: 'center', color: COR.muted, fontSize: '13px' }}>
                Nenhum acesso encontrado.
              </div>
            ) : acessosDoPar.map(a => {
              const exp = expandidos.has(a.id);
              const tipoLabel = TIPOS_ACESSO.find(t => t.codigo === a.tipo_acesso)?.label || a.tipo_acesso;
              return (
                <div key={a.id} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                  <div onClick={() => toggleExpandir(a.id)} style={{
                    padding: '10px 14px', cursor: 'pointer',
                    // r-now: 60px 1fr 2fr 100px alinhado com o header
                    display: 'grid', gridTemplateColumns: '60px 1fr 2fr 130px 100px', gap: '10px',
                    alignItems: 'center', fontSize: '13px',
                    backgroundColor: exp ? COR.azulBg : COR.branco,
                  }}>
                    <div style={{ color: COR.muted, fontWeight: '600' }}>{a.seq}</div>
                    {/* r98: ícone SVG específico de cada tipo + label */}
                    <div style={{
                      color: COR.texto, fontWeight: '600',
                      display: 'flex', alignItems: 'center', gap: '8px',
                    }}>
                      {iconeDoTipo(a.tipo_acesso, { width: 16, height: 16, color: COR.azul })}
                      <span>{tipoLabel}</span>
                    </div>
                    <div style={{ color: COR.texto }}>{a.descricao}</div>
                    <div><PilulaVisibilidade valor={a.visibilidade} /></div>
                    <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                      <SePode rotina="acessos" acao="alterar">
                        <BtnIcone icone="alterar" onClick={() => abrirEditar(a)} title="Alterar"
                                  cor={COR.azul} />
                      </SePode>
                      <SePode rotina="acessos" acao="excluir">
                        <BtnIcone icone="excluir" onClick={() => excluir(a)} title="Excluir"
                                  cor={COR.vermelho} />
                      </SePode>
                      {/* r94: botão SVG de expansão, mesmo tamanho/padrão dos BtnIcone */}
                      <button
                        onClick={() => toggleExpandir(a.id)}
                        title={exp ? 'Recolher' : 'Expandir'}
                        style={{
                          width: '28px', height: '28px',
                          border: 'none', background: 'transparent',
                          color: COR.muted, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          padding: 0, borderRadius: '4px',
                          transition: 'background 0.15s, color 0.15s',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = COR.azulBg;
                          e.currentTarget.style.color = COR.azul;
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = COR.muted;
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.5"
                             strokeLinecap="round" strokeLinejoin="round"
                             style={{
                               transform: exp ? 'rotate(180deg)' : 'rotate(0deg)',
                               transition: 'transform 0.2s',
                             }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {exp && (
                    <DetalhesAtributos acesso={a} atributos={a.atributos || []} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal de inclusão/edição */}
      {modalAberto && (
        <AcessoModal
          modo={modoModal}
          acesso={acessoEdit}
          // r94: NÃO pré-seleciona usuário/cliente no modo "novo" — começa em branco
          colaboradorPreSel={modoModal === 'editar' ? parSelecionado?.colaborador_id : null}
          clientePreSel={modoModal === 'editar' ? parSelecionado?.cliente_id : null}
          onFechar={() => setModalAberto(false)}
          onSalvou={carregar}
        />
      )}
    </div>
  );
}

// ── Subcomponente: tag colorida do cliente (mesmo formato do cadastro) ─────
// r94: replicado do ClientesPage.jsx pra manter identidade visual.
function TagCliente({ tag, cor }) {
  if (!tag) return null;
  const bgCor = cor || '#003399';
  // Calcula luminância pra escolher cor do texto (preto se fundo claro)
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const corTexto = lum > 0.5 ? '#1a1a2e' : '#ffffff';

  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: bgCor, color: corTexto,
      letterSpacing: '0.3px',
    }}>{tag}</span>
  );
}

// ── r245: pílula Restrito / Compartilhado ──────────────────────────────────
function PilulaVisibilidade({ valor }) {
  const v = VISIBILIDADES[valor] || VISIBILIDADES[VISIBILIDADE_PADRAO];
  return (
    <span title={valor === 'R'
            ? 'Visível só para quem restringiu e para administradores'
            : 'Visível para todos com acesso à rotina'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            height: '20px', padding: '0 9px', boxSizing: 'border-box',
            borderRadius: '10px', fontSize: '11px', fontWeight: '700',
            lineHeight: 1, whiteSpace: 'nowrap',
            backgroundColor: v.bg, color: v.cor, border: `1px solid ${v.cor}`,
            fontFamily: "'Manrope', sans-serif",
          }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.5"
           strokeLinecap="round" strokeLinejoin="round">
        {valor === 'R' ? (
          <>
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </>
        ) : (
          <>
            <circle cx="9" cy="8" r="3.5" />
            <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" />
            <path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14c2.5.6 4 2.8 4 6" />
          </>
        )}
      </svg>
      {v.label}
    </span>
  );
}

// Valor do atributo é uma URL? (http/https ou começando com www.)
// Devolve a URL pronta pra abrir, ou null. IP puro não conta como URL.
function urlDoValor(valor) {
  const v = (valor || '').trim();
  if (/^https?:\/\/\S+$/i.test(v)) return v;
  if (/^www\.\S+$/i.test(v)) return `https://${v}`;
  return null;
}

// ── Subcomponente: lista de atributos de 1 acesso (linha expandida) ────────
function DetalhesAtributos({ acesso, atributos }) {
  const [senhasVisiveis, setSenhasVisiveis] = useState({});
  // r243: erro do download fica na própria linha — não há modal aberto aqui
  const [erroRdp, setErroRdp] = useState('');
  // r138: feedback visual de copiar (qual atributo foi copiado por último)
  const [copiado, setCopiado] = useState(null);

  // r138: copia texto pro clipboard com fallback pra contextos não-https
  const copiarValor = async (tipo, valor) => {
    if (!valor) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(valor);
      } else {
        // Fallback: textarea invisível + execCommand
        const ta = document.createElement('textarea');
        ta.value = valor;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiado(tipo);
      setTimeout(() => setCopiado(c => c === tipo ? null : c), 1500);
    } catch (err) {
      console.error('Falha ao copiar:', err);
    }
  };

  // r243: download do Arquivo Rdp direto do browse — é o "a qualquer momento"
  // do pedido: não precisa abrir o modal de alteração pra pegar o arquivo.
  const baixarRdp = async () => {
    setErroRdp('');
    try {
      await acessoService.baixarRdp(acesso.id, acesso.rdp_nome);
    } catch (err) {
      setErroRdp(err.response?.data?.mensagem || 'Erro ao baixar o arquivo.');
    }
  };

  const linhaRdp = acesso?.rdp_nome ? (
    <div style={{
      display: 'grid', gridTemplateColumns: '120px 1fr',
      padding: '4px 0', fontSize: '12px',
    }}>
      <div style={{
        color: COR.verde, fontWeight: '600',
        display: 'flex', alignItems: 'center', gap: '4px',
      }}>
        ✓ Arquivo Rdp
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={baixarRdp} title="Baixar o arquivo .rdp" style={{
          padding: '2px 10px', fontSize: '11px', fontWeight: '600',
          border: `1px solid ${COR.azul}`, borderRadius: '4px',
          backgroundColor: COR.azulBg, color: COR.azul, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '5px',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" />
            <path d="M7 11l5 5 5-5" />
            <path d="M4 20h16" />
          </svg>
          Baixar
        </button>
        <span style={{ color: COR.texto }}>{acesso.rdp_nome}</span>
        {erroRdp && (
          <span style={{ color: COR.vermelho, fontWeight: '600' }}>{erroRdp}</span>
        )}
      </div>
    </div>
  ) : null;

  if (atributos.length === 0) {
    return (
      <div style={{ padding: '6px 14px 12px 60px', backgroundColor: COR.bg }}>
        {linhaRdp}
        <div style={{ padding: '4px 0', fontSize: '12px', color: COR.muted, fontStyle: 'italic' }}>
          Sem atributos cadastrados.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '6px 14px 12px 60px', backgroundColor: COR.bg }}>
      {linhaRdp}
      {atributos.map(a => {
        const isSenha = ehSenha(a.tipo_attr);
        const visivel = senhasVisiveis[a.tipo_attr];
        const temValor = !!a.valor;
        const foiCopiado = copiado === a.tipo_attr;
        return (
          <div key={a.tipo_attr} style={{
            display: 'grid', gridTemplateColumns: '120px 1fr',
            padding: '4px 0', fontSize: '12px',
          }}>
            <div style={{
              color: COR.verde, fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              ✓ {LABELS_ATRIBUTO[a.tipo_attr] || a.tipo_attr}
            </div>
            <div style={{ color: COR.texto }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>{isSenha ? (visivel ? a.valor : (temValor ? '••••••••' : '')) : a.valor}</span>

                {/* r138: botão olho (só pra senha) */}
                {isSenha && temValor && (
                  <button onClick={() => setSenhasVisiveis(s => ({
                    ...s, [a.tipo_attr]: !s[a.tipo_attr],
                  }))} style={{
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', fontSize: '12px', padding: '0 4px',
                  }} title={visivel ? 'Ocultar' : 'Mostrar'}>
                    {visivel ? '🙈' : '👁'}
                  </button>
                )}

                {/* r138: botão copiar */}
                {temValor && (
                  <button
                    onClick={() => copiarValor(a.tipo_attr, a.valor)}
                    title={foiCopiado ? 'Copiado!' : 'Copiar'}
                    style={{
                      border: 'none', background: 'transparent',
                      cursor: 'pointer', padding: '2px 4px',
                      display: 'inline-flex', alignItems: 'center',
                      color: foiCopiado ? COR.verde : COR.muted,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { if (!foiCopiado) e.currentTarget.style.color = COR.azul; }}
                    onMouseLeave={e => { if (!foiCopiado) e.currentTarget.style.color = COR.muted; }}
                  >
                    {foiCopiado ? (
                      // ícone de check
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="3"
                           strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      // ícone de copiar (2 retângulos sobrepostos)
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    )}
                  </button>
                )}

                {/* botão abrir URL no navegador (só quando o valor é uma URL) */}
                {!isSenha && urlDoValor(a.valor) && (
                  <button
                    onClick={() => window.open(urlDoValor(a.valor), '_blank', 'noopener,noreferrer')}
                    title="Abrir no navegador"
                    style={{
                      border: 'none', background: 'transparent',
                      cursor: 'pointer', padding: '2px 4px',
                      display: 'inline-flex', alignItems: 'center',
                      color: COR.muted,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = COR.azul; }}
                    onMouseLeave={e => { e.currentTarget.style.color = COR.muted; }}
                  >
                    {/* ícone de link externo */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Subcomponente: dropdown multi-seleção compacto ────────────────────────
// ── Subcomponente: dropdown multi-seleção com busca interna ─────────────────
// r95: adicionado input de pesquisa no topo do dropdown (filtragem por
//      substring, case-insensitive, focus automático ao abrir).
function MultiSelect({ label, opcoes, selecionados, onToggle }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = React.useRef(null);
  const inputBuscaRef = React.useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    const fora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setAberto(false);
        setBusca('');                                // reset ao fechar
      }
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  // Foca o campo de busca quando o dropdown abre
  useEffect(() => {
    if (aberto && inputBuscaRef.current) {
      setTimeout(() => inputBuscaRef.current?.focus(), 50);
    }
  }, [aberto]);

  const labelBotao = selecionados.length === 0
    ? `${label} (todos)`
    : `${label} (${selecionados.length})`;

  // Filtra opções por substring no label
  const opcoesFiltradas = busca.trim() === ''
    ? opcoes
    : opcoes.filter(op =>
        (op.label || '').toLowerCase().includes(busca.trim().toLowerCase())
      );

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '200px' }}>
      <button onClick={() => setAberto(a => !a)} style={{
        ...inputStyle, cursor: 'pointer',
        textAlign: 'left', width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>{labelBotao}</span>
        <span style={{ color: COR.muted, fontSize: '10px' }}>{aberto ? '▲' : '▼'}</span>
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
          backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
          borderRadius: '5px', zIndex: 50,
          maxHeight: '320px', display: 'flex', flexDirection: 'column',
          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
        }}>
          {/* Campo de busca fixo no topo */}
          <div style={{
            padding: '8px', borderBottom: `1px solid ${COR.borda}`,
            backgroundColor: COR.bg,
          }}>
            <input
              ref={inputBuscaRef}
              type="text"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder={`Pesquisar ${label.toLowerCase()}...`}
              style={{
                ...inputStyle, height: '30px', fontSize: '12px',
              }}
            />
          </div>

          {/* Lista de opções (scroll dentro da área restante) */}
          <div style={{ overflowY: 'auto', flex: 1, maxHeight: '260px' }}>
            {opcoesFiltradas.length === 0 ? (
              <div style={{ padding: '12px', color: COR.muted, fontSize: '12px', textAlign: 'center' }}>
                {opcoes.length === 0 ? 'Sem opções.' : 'Nenhum resultado.'}
              </div>
            ) : opcoesFiltradas.map(op => (
              <label key={op.id} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 10px', cursor: 'pointer',
                fontSize: '12px', color: COR.texto,
                borderBottom: `1px solid ${COR.borda}`,
              }}>
                <input
                  type="checkbox"
                  checked={selecionados.includes(op.id)}
                  onChange={() => onToggle(op.id)}
                />
                <span>{op.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AcessosPage;
